import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetAccreditations, dataSetFarms } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import AccreditationHttpService from '../../../services/http/masterData/accreditationHttpService';
import Screen from '../../../components/Screen';
import AccreditationForm from './form/AccreditationForm';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import FarmHttpService from '../../../services/http/masterData/farmHttpService';
import { setAccreditationMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { CustomTable } from '../../../components/table/CustomTableWrapper';
import { IApiError } from '../../../@types/other';
import { IAccreditation } from '../../../@types/model/masterData/accreditation/accreditation';
import { IAccreditationFormValues } from '../../../@types/model/masterData/accreditation/accreditationFormValues';
import AccreditationModelHelper from '../../../@types/model/masterData/accreditation/accreditationModelHelper';
import { useLocation } from 'react-router-dom';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const AccreditationScreen = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const accreditations = useAppSelector(x => x.masterData.accreditations);
    const farms = useAppSelector(x => x.masterData.farms);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedAccreditation, setSelectedAccreditation] = useState<IAccreditation | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingAccreditation, setDeletingAccreditation] = useState<IAccreditation | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await AccreditationHttpService.getAccreditationData();
                    const res2 = await FarmHttpService.getFarmData();
                    dispatch(dataSetAccreditations(res.data));
                    dispatch(dataSetFarms(res2.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError | undefined;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading accreditations.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : IAccreditationFormValues) => {
        setIsLoading(true);

        try {
            const upsert = AccreditationModelHelper.createUpsert(values);
            const res = await AccreditationHttpService.addOrUpdateAccreditation(upsert);
            const updatedList = upsertArrayElement(accreditations, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetAccreditations(updatedList));
            await setAccreditationMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedAccreditation ? 'Accreditation updated successfully.' : 'Accreditation added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating accreditation data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeAccreditation = async () => {
        if (!deletingAccreditation) return;
        const updatedAccreditation = { ...deletingAccreditation, isActive: false };
        setIsLoading(true);
        try {
            const res = await AccreditationHttpService.deleteAccreditation(updatedAccreditation.id);
            const updatedList = upsertArrayElement(accreditations, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetAccreditations(updatedList));
            await setAccreditationMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Accreditation updated successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting accreditation.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedAccreditation(null);
    };

    const edit = (selectedRow : IAccreditation) => {
        setSelectedAccreditation(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IAccreditation) : void => {
        setDeletingAccreditation(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingAccreditation(null);
    };

    const deleteButtonTooltip = (row : IAccreditation) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Accreditation', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatFarms = useCallback((farmIds : Array<number>) => {
        return farms.filter(farm => farmIds.includes(farm.id))
            .map(farm => farm.name).join(', ');
    }, [farms]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return AccreditationModelHelper.createFormValues(selectedAccreditation, farms);
        }
    }, [selectedAccreditation, isDialogOpen, farms]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IAccreditation>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Accreditation', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Accreditation', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IAccreditation) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Farms', field: 'farmIds', formatFunction: formatFarms, width: 200, enableFiltering: true, enableSorting: true },
                            { title: 'Issue Date', field: 'issueDate', type: 'date', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Expiration Date', field: 'expirationDate', type: 'date', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={accreditations}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedAccreditation ? 'Edit Accreditation' : 'Add Accreditation'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <AccreditationForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Accreditation'}
                open={!!deletingAccreditation}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeAccreditation}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default AccreditationScreen;
